import { commaThousondSeperator } from '@shared/lib';

import type { NumberInputFormat } from './model/types';

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

const isDigit = (char: string) => /[0-9۰-۹٠-٩]/.test(char);
const isDecimalSeparator = (char: string) => char === '.' || char === '٫';
const isCursorSignificant = (char: string, format: NumberInputFormat) =>
  isDigit(char) || (format === 'decimal' && isDecimalSeparator(char));

export const toEnglishNumberChars = (value: string) =>
  value
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - '۰'.charCodeAt(0)))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - '٠'.charCodeAt(0)))
    .replace('٫', '.');

export const countCursorSignificantChars = (value: string, format: NumberInputFormat) =>
  [...value].filter((char) => isCursorSignificant(char, format)).length;

export const cursorPosAfterSignificantChars = (
  formatted: string,
  significantCount: number,
  format: NumberInputFormat
) => {
  if (significantCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (isCursorSignificant(formatted[i]!, format)) {
      seen += 1;
      if (seen === significantCount) return i + 1;
    }
  }
  return formatted.length;
};

export const sanitizeNumberInput = (
  rawValue: string,
  format: NumberInputFormat,
  decimalPlaces: number
): string | null => {
  if (format === 'integer') {
    let digits = toEnglishNumberChars(rawValue).replace(/[^\d]/g, '');
    if (digits.length > 1 && digits.startsWith('0')) {
      digits = digits.replace(/^0+/, '') || '0';
    }
    return digits;
  }

  const sanitized = toEnglishNumberChars(rawValue).replace(/[^0-9.]/g, '');
  const match = sanitized.match(/^(\d+)?(\.\d*)?$/);
  if (!match) return null;

  if (!sanitized.includes('.')) return sanitized;

  const [integerPart = '', fractionPart = ''] = sanitized.split('.');
  return `${integerPart}.${fractionPart.slice(0, decimalPlaces)}`;
};

export const formatNumberInputValue = (value: string, format: NumberInputFormat): string => {
  if (!value) return '';

  if (format === 'integer') {
    return commaThousondSeperator(+value);
  }

  if (!value.includes('.')) {
    return Number(value).toLocaleString('fa-IR');
  }

  const [integerPart, fractionPart = ''] = value.split('.');
  const persianFraction = fractionPart.replace(/\d/g, (d) => PERSIAN_DIGITS[+d]!);

  if (!integerPart) return `٫${persianFraction}`;
  return `${Number(integerPart).toLocaleString('fa-IR')}٫${persianFraction}`;
};
